import { useRef, useState, useCallback } from "react";
import {
  Box,
  Button,
  IconButton,
  TextField,
  Typography,
  Paper,
  Chip,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { Add, Delete, Image as ImageIcon } from "@mui/icons-material";
import { toast } from "react-toastify";
import api, { resolveQuestionHtmlAssetUrl } from "../../../api";
import FillInBlankEditor, { BlankAnswerEditor, SpecialCharPanel } from "./FillInBlankEditor";
import { getFixedMarksPerBlankLabel } from "../../../utils/fillQuestionUtils";

const DEFAULT_BOX = () => ({ x: 0.4, y: 0.42, w: 0.12, h: 0.1 });
const clamp01 = (v) => Math.max(0, Math.min(1, v));

const ImageOverlayFillEditor = ({
  blanks,
  setBlanks,
  blankMarks,
  setBlankMarks,
  blankLabels,
  setBlankLabels,
  overlayLayout,
  setOverlayLayout,
  attachedImages = [],
  fillMarksMode = "sum",
  fillFixedTotal = 1,
}) => {
  const fixedMode = fillMarksMode === "fixed";
  const fileRef = useRef(null);
  const canvasRef = useRef(null);
  const dragRef = useRef(null);
  const charTargetRef = useRef(null);
  const insertIntoActive = useCallback((char) => charTargetRef.current?.(char), []);
  const [uploading, setUploading] = useState(false);
  const [urlDraft, setUrlDraft] = useState("");

  const mode = overlayLayout?.mode === "inline" ? "inline" : "boxes";
  const imageUrl = overlayLayout?.image_url || "";
  const boxes = Array.isArray(overlayLayout?.blanks) ? overlayLayout.blanks : [];

  const setMode = (next) => {
    if (!next || next === mode) return;
    setOverlayLayout({ ...(overlayLayout || {}), mode: next, blanks: boxes });
  };

  const setImage = (url) => setOverlayLayout({ ...(overlayLayout || {}), image_url: url, blanks: boxes });

  const syncArrays = (n) => {
    setBlanks(Array.from({ length: n }, (_, i) => {
      const existing = blanks?.[i];
      return Array.isArray(existing) && existing.length > 0 ? [...existing] : [""];
    }));
    setBlankMarks(Array.from({ length: n }, (_, i) => {
      const m = blankMarks?.[i];
      return m != null && m !== "" ? Math.max(1, parseInt(m, 10) || 1) : 1;
    }));
    if (setBlankLabels) {
      setBlankLabels(Array.from({ length: n }, (_, i) => blankLabels?.[i] ?? ""));
    }
  };

  const applyBoxes = (nextBoxes) => {
    setOverlayLayout({ ...(overlayLayout || {}), image_url: imageUrl, blanks: nextBoxes });
    syncArrays(nextBoxes.length);
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (fileRef.current) fileRef.current.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("image", file);
      const res = await api.post("/admin/question-bank/upload-image", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const url = res.data?.data?.image_url || "";
      if (url) {
        setImage(url);
        toast.success("Image uploaded");
      }
    } catch {
      toast.error("Image upload failed");
    } finally {
      setUploading(false);
    }
  };

  const addBox = () => applyBoxes([...boxes, DEFAULT_BOX()]);

  const removeBox = (idx) => {
    const next = boxes.filter((_, i) => i !== idx);
    setOverlayLayout({ ...(overlayLayout || {}), image_url: imageUrl, blanks: next });
    setBlanks((blanks || []).filter((_, i) => i !== idx));
    setBlankMarks((blankMarks || []).filter((_, i) => i !== idx));
    if (setBlankLabels) setBlankLabels((blankLabels || []).filter((_, i) => i !== idx));
  };

  const updateBox = (idx, patch) => {
    const next = boxes.map((b, i) => (i === idx ? { ...b, ...patch } : b));
    setOverlayLayout({ ...(overlayLayout || {}), image_url: imageUrl, blanks: next });
  };

  const onPointerMove = (e) => {
    const drag = dragRef.current;
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!drag || !rect) return;
    const dx = (e.clientX - drag.startX) / rect.width;
    const dy = (e.clientY - drag.startY) / rect.height;
    if (drag.mode === "move") {
      updateBox(drag.idx, {
        x: clamp01(drag.box.x + dx),
        y: clamp01(drag.box.y + dy),
      });
    } else {
      updateBox(drag.idx, {
        w: Math.max(0.03, Math.min(1 - drag.box.x, drag.box.w + dx)),
        h: Math.max(0.03, Math.min(1 - drag.box.y, drag.box.h + dy)),
      });
    }
  };

  const endDrag = () => {
    dragRef.current = null;
    window.removeEventListener("pointermove", onPointerMove);
    window.removeEventListener("pointerup", endDrag);
  };

  const startDrag = (e, idx, mode) => {
    e.preventDefault();
    e.stopPropagation();
    dragRef.current = { idx, mode, startX: e.clientX, startY: e.clientY, box: { ...boxes[idx] } };
    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", endDrag);
  };

  const updateBlankAnswer = (idx, ansIdx, value) => {
    const next = [...(blanks || [])];
    while (next.length <= idx) next.push([""]);
    const row = [...(next[idx] || [""])];
    row[ansIdx] = value;
    next[idx] = row;
    setBlanks(next);
  };

  const addAcceptedAnswer = (idx) => {
    const next = [...(blanks || [])];
    while (next.length <= idx) next.push([""]);
    next[idx] = [...(next[idx] || [""]), ""];
    setBlanks(next);
  };

  const removeAcceptedAnswer = (idx, ansIdx) => {
    const next = [...(blanks || [])];
    const row = [...(next[idx] || [""])];
    if (row.length <= 1) return;
    row.splice(ansIdx, 1);
    next[idx] = row;
    setBlanks(next);
  };

  const updateBlankMark = (idx, value) => {
    const next = [...(blankMarks || [])];
    while (next.length <= idx) next.push(1);
    next[idx] = Math.max(0, Number(value) || 0);
    setBlankMarks(next);
  };

  return (
    <Box>
      <ToggleButtonGroup
        exclusive
        size="small"
        value={mode}
        onChange={(_, v) => setMode(v)}
        sx={{ mb: 1.5 }}
      >
        <ToggleButton value="boxes" sx={{ textTransform: "none" }}>Drag boxes on image</ToggleButton>
        <ToggleButton value="inline" sx={{ textTransform: "none" }}>Inline blanks (in text)</ToggleButton>
      </ToggleButtonGroup>

      {mode === "inline" ? (
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Drag the question image into the <strong>Question text</strong> editor above and type a
            blank marker <strong>□</strong> (use the math tools) wherever a student must fill in an
            answer. Then enter the accepted answer for each blank below, in the order the □ appear.
          </Typography>
          <FillInBlankEditor
            blanks={blanks}
            setBlanks={setBlanks}
            isMulti
            blankLabels={blankLabels}
            setBlankLabels={setBlankLabels}
            blankMarks={blankMarks}
            setBlankMarks={setBlankMarks}
            fillMarksMode={fillMarksMode}
            fillFixedTotal={fillFixedTotal}
          />
        </Box>
      ) : (
      <>
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
        Image with answer boxes — upload (or paste a URL for) the question image, then drag blank
        boxes onto each gap the student must fill.
      </Typography>

      <input ref={fileRef} type="file" accept="image/*" hidden onChange={handleFile} />
      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center", mb: 2 }}>
        <Button
          variant="outlined"
          size="small"
          startIcon={uploading ? <CircularProgress size={16} /> : <ImageIcon />}
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          sx={{ textTransform: "none" }}
        >
          {imageUrl ? "Replace image" : "Upload image"}
        </Button>
        <TextField
          size="small"
          placeholder="…or paste an image URL"
          value={urlDraft}
          onChange={(e) => setUrlDraft(e.target.value)}
          sx={{ flex: 1, minWidth: 200 }}
        />
        <Button
          size="small"
          variant="text"
          onClick={() => { if (urlDraft.trim()) { setImage(urlDraft.trim()); setUrlDraft(""); } }}
          disabled={!urlDraft.trim()}
          sx={{ textTransform: "none" }}
        >
          Use URL
        </Button>
        {imageUrl ? (
          <Button size="small" startIcon={<Add />} onClick={addBox} sx={{ textTransform: "none" }}>
            Add box
          </Button>
        ) : null}
        <Chip label={`${boxes.length} box${boxes.length !== 1 ? "es" : ""}`} size="small" color="primary" variant="outlined" />
      </Box>

      {attachedImages.filter(Boolean).length > 0 ? (
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.75 }}>
            …or use an image already attached to this question
          </Typography>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            {[...new Set(attachedImages.filter(Boolean))].map((u) => {
              const selected = u === imageUrl;
              return (
                <Box
                  key={u}
                  onClick={() => setImage(u)}
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: 1,
                    overflow: "hidden",
                    cursor: "pointer",
                    flexShrink: 0,
                    border: selected ? "2px solid #6366f1" : "2px solid #e0e0e0",
                    boxShadow: selected ? "0 0 0 2px rgba(99,102,241,0.25)" : "none",
                  }}
                >
                  <img
                    src={resolveQuestionHtmlAssetUrl(u) || u}
                    alt="Attached"
                    draggable={false}
                    style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                  />
                </Box>
              );
            })}
          </Box>
        </Box>
      ) : null}

      {imageUrl ? (
        <Paper
          variant="outlined"
          ref={canvasRef}
          sx={{ position: "relative", display: "inline-block", maxWidth: "100%", mb: 3, userSelect: "none", lineHeight: 0 }}
        >
          <img
            src={resolveQuestionHtmlAssetUrl(imageUrl) || imageUrl}
            alt="Question"
            draggable={false}
            style={{ maxWidth: "100%", display: "block", pointerEvents: "none" }}
          />
          {boxes.map((b, idx) => {
            const lineRows = Math.max(1, parseInt(b.lines, 10) || 1);
            const rowGap = Math.max(16, parseInt(b.row_gap, 10) || 40);
            const isLineStyle = b.style === "line";
            const lineBg = isLineStyle && lineRows > 1
              ? {
                  background: `repeating-linear-gradient(to bottom, transparent 0, transparent calc(${100 / lineRows}% - 2px), #6366f1 calc(${100 / lineRows}% - 2px), #6366f1 ${100 / lineRows}%)`,
                  borderBottom: "none",
                }
              : isLineStyle
                ? { borderBottom: "2px dashed #6366f1", background: "transparent" }
                : { border: "2px solid #6366f1", background: "rgba(99,102,241,0.12)" };
            return (
            <Box
              key={idx}
              onPointerDown={(e) => startDrag(e, idx, "move")}
              sx={{
                position: "absolute",
                left: `${(b.x ?? 0) * 100}%`,
                top: `${(b.y ?? 0) * 100}%`,
                width: `${(b.w ?? 0.1) * 100}%`,
                height: `${(b.h ?? 0.08) * 100}%`,
                ...(isLineStyle && lineRows > 1 ? { minHeight: `${lineRows * rowGap}px` } : {}),
                cursor: "move",
                boxSizing: "border-box",
                display: "flex",
                alignItems: isLineStyle ? "flex-end" : "center",
                justifyContent: "center",
                ...lineBg,
              }}
            >
              <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, color: "#4338ca" }}>{idx + 1}</Typography>
              <Box
                onPointerDown={(e) => startDrag(e, idx, "resize")}
                sx={{ position: "absolute", right: -6, bottom: -6, width: 12, height: 12, bgcolor: "#6366f1", borderRadius: "50%", cursor: "nwse-resize" }}
              />
            </Box>
            );
          })}
        </Paper>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Upload or paste an image first.
        </Typography>
      )}

      {boxes.length > 0 ? (
        <>
          <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
            Correct answer per box
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
            A box can accept several values — add alternatives below. The order students type does
            not matter across boxes; each box is graded against its own answers.
          </Typography>
          <SpecialCharPanel onInsert={insertIntoActive} />
          {boxes.map((_, idx) => (
            <Box key={idx} sx={{ mb: 2, p: 2, border: "1px solid #e0e0e0", borderRadius: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                <Chip
                  label={`Box ${idx + 1}`}
                  size="small"
                  sx={{ background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)", color: "#fff", fontWeight: 500 }}
                />
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={boxes[idx]?.style === "line" ? "line" : "box"}
                  onChange={(_, v) => v && updateBox(idx, { style: v })}
                >
                  <ToggleButton value="box" sx={{ textTransform: "none", py: 0.25, px: 1 }}>Box</ToggleButton>
                  <ToggleButton value="line" sx={{ textTransform: "none", py: 0.25, px: 1 }}>Line</ToggleButton>
                </ToggleButtonGroup>
                {fixedMode ? (
                  <Chip
                    label={getFixedMarksPerBlankLabel(fillFixedTotal, boxes.length)}
                    size="small"
                    color="primary"
                    variant="outlined"
                    sx={{ fontWeight: 700 }}
                  />
                ) : (
                  <TextField
                    type="number"
                    size="small"
                    label="Marks"
                    value={blankMarks?.[idx] ?? 1}
                    onChange={(e) => updateBlankMark(idx, e.target.value)}
                    inputProps={{ min: 0, step: 0.25 }}
                    sx={{ width: 88 }}
                  />
                )}
                <TextField
                  type="number"
                  size="small"
                  label="Lines"
                  value={boxes[idx]?.lines ?? 1}
                  onChange={(e) => updateBox(idx, { lines: Math.max(1, Math.min(8, parseInt(e.target.value, 10) || 1)) })}
                  inputProps={{ min: 1, max: 8 }}
                  helperText="Writing rows"
                  sx={{ width: 88, '& .MuiFormHelperText-root': { mx: 0, fontSize: '0.65rem', whiteSpace: 'nowrap' } }}
                />
                {(parseInt(boxes[idx]?.lines, 10) || 1) > 1 ? (
                  <TextField
                    type="number"
                    size="small"
                    label="Row gap"
                    value={boxes[idx]?.row_gap ?? 40}
                    onChange={(e) => updateBox(idx, { row_gap: Math.max(16, Math.min(120, parseInt(e.target.value, 10) || 40)) })}
                    inputProps={{ min: 16, max: 120, step: 4 }}
                    helperText="px per line"
                    sx={{ width: 92, '& .MuiFormHelperText-root': { mx: 0, fontSize: '0.65rem', whiteSpace: 'nowrap' } }}
                  />
                ) : null}
                {setBlankLabels ? (
                  <TextField
                    size="small"
                    label="Pre-filled (shown, not graded)"
                    value={blankLabels?.[idx] ?? ""}
                    onChange={(e) => {
                      const next = [...(blankLabels || [])];
                      while (next.length <= idx) next.push("");
                      next[idx] = e.target.value;
                      setBlankLabels(next);
                    }}
                    sx={{ flex: 1 }}
                  />
                ) : null}
                <IconButton size="small" color="error" onClick={() => removeBox(idx)} sx={{ ml: "auto" }}>
                  <Delete fontSize="small" />
                </IconButton>
              </Box>
              {(blanks?.[idx] || [""]).map((ans, ansIdx) => (
                <Box key={ansIdx} sx={{ display: "flex", alignItems: "flex-start", gap: 0.5, mb: 1 }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.4, fontWeight: 500 }}>
                      {ansIdx === 0 ? "Primary answer" : `Accepted answer ${ansIdx + 1}`}
                    </Typography>
                    <BlankAnswerEditor
                      value={ans}
                      onChange={(val) => updateBlankAnswer(idx, ansIdx, val)}
                      placeholder={ansIdx === 0 ? "e.g. 3" : `Alternative ${ansIdx + 1}`}
                      charTargetRef={charTargetRef}
                    />
                  </Box>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => removeAcceptedAnswer(idx, ansIdx)}
                    disabled={(blanks?.[idx] || []).length <= 1}
                    sx={{ mt: 3.5 }}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </Box>
              ))}
              <Button size="small" startIcon={<Add />} onClick={() => addAcceptedAnswer(idx)} sx={{ mt: 0.5 }}>
                Add Alternative
              </Button>
            </Box>
          ))}
        </>
      ) : null}
      </>
      )}
    </Box>
  );
};

export default ImageOverlayFillEditor;
